import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, TrendingUp, AlertTriangle } from 'lucide-react';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { RulePreviewBadge } from './RulePreviewBadge';
import { AutomationItemsPanel } from './AutomationItemsPanel';
import { MarkupSimulationModal } from './MarkupSimulationModal';
import type { Simulation } from './MarkupSimulationModal';

interface Rule {
  id: number; seller_id: number; min_price_usd: number; max_price_usd: number;
  markup_type: string; markup_value: number; max_price_ml: number | null;
  // 2026-09-18 — piloto escalonado (ver markup-rollouts)
  pilot_markup_type?: string | null; pilot_markup_value?: number | null; pilot_pct?: number | null;
  pilot_started_at?: string | null; pilot_rollout_id?: number | null;
}

interface Rollout {
  id: number; rule_id: number; old_markup_type: string; old_markup_value: number;
  new_markup_type: string; new_markup_value: number; range_min_usd: number | null; range_max_usd: number | null;
  pilot_pct: number; status: 'pilot' | 'extended' | 'reverted'; started_at: string; evaluate_at: string;
  evaluated_at: string | null; recommendation: string | null;
  pilot_metrics: null | { pilot: GroupMetrics; control: GroupMetrics };
}
interface GroupMetrics {
  asins: number; units_before: number; units_after: number; visits_before: number; visits_after: number;
  units_ratio: number | null; visits_ratio: number | null;
}

const fmtMarkup = (type: string, value: number | string) =>
  type === 'percentage' ? `${value}%` : `$${Number(value).toFixed(2)}`;
const fmtRatio = (r: number | null) => (r == null ? 's/d' : `${Math.round(r * 100)}%`);
const fmtDate = (s: string) => new Date(s).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function MarkupRulesTab({ t: _t }: { t: (k: string) => string }) {
  const { sellerId } = useUIStore();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ min_price_usd: '', max_price_usd: '', markup_type: 'percentage', markup_value: '', max_price_ml: '' });
  const [saving, setSaving] = useState(false);
  const [autoStatus, setAutoStatus] = useState<{ enabled: boolean; stats: { active: number; error: number }; total_eligible: number } | null>(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [rollouts, setRollouts] = useState<Rollout[]>([]);
  // Simulación previa al guardado (2026-09-18)
  const [simOpen, setSimOpen] = useState(false);
  const [sim, setSim] = useState<Simulation | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [pilotPct, setPilotPct] = useState(10);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [rulesRes, statusRes, rollRes] = await Promise.all([
      api.get<Rule[]>(`/mercadolibre/markup-rules?seller_id=${sellerId}`),
      api.get<any>(`/pricing-automation/status?seller_id=${sellerId}`),
      api.get<Rollout[]>(`/mercadolibre/markup-rules/rollouts?seller_id=${sellerId}`),
    ]);
    if (rulesRes.success && rulesRes.data) setRules(rulesRes.data as unknown as Rule[]);
    if (statusRes.success && statusRes.data) setAutoStatus(statusRes.data as any);
    if (rollRes.success && rollRes.data) setRollouts(rollRes.data as unknown as Rollout[]);
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const handleToggleAutomation = async () => {
    if (!confirm(autoStatus?.enabled
      ? 'Desactivar ML Pricing Automation? Se restaura el pricing manual.'
      : `Activar ML Pricing Automation para ${autoStatus?.total_eligible || 0} items elegibles?`
    )) return;
    setAutoLoading(true);
    if (autoStatus?.enabled) {
      await api.post(`/pricing-automation/deactivate?seller_id=${sellerId}`, {});
    } else {
      await api.post(`/pricing-automation/activate?seller_id=${sellerId}`, {});
    }
    setAutoLoading(false);
    load();
  };

  const editingRule = editing ? rules.find((r) => r.id === editing) : undefined;
  const markupChanged = !!editingRule && (
    editingRule.markup_type !== form.markup_type ||
    Math.abs(Number(editingRule.markup_value) - parseFloat(form.markup_value || '0')) > 0.0001
  );

  // 2026-09-18: antes de guardar se simula el efecto. Si cambia el recargo, el guardado
  // arranca como piloto (10% / 72 h) salvo que se elija "Aplicar a todos ahora".
  const openSimulation = async () => {
    if (!form.min_price_usd || !form.max_price_usd || !form.markup_value) return;
    setSim(null); setSimLoading(true); setSimOpen(true);
    const res = await api.post<Simulation>('/mercadolibre/markup-rules/simulate', {
      seller_id: sellerId,
      rules: [{
        id: editing ?? undefined,
        min_price_usd: parseFloat(form.min_price_usd),
        max_price_usd: parseFloat(form.max_price_usd),
        markup_type: form.markup_type,
        markup_value: parseFloat(form.markup_value),
      }],
    });
    if (res.success && res.data) setSim(res.data as unknown as Simulation);
    setSimLoading(false);
  };

  const handleSave = async (applyNow: boolean) => {
    if (!form.min_price_usd || !form.max_price_usd || !form.markup_value) return;
    setSaving(true);
    if (editing) {
      const res = await api.put<Rule>(`/mercadolibre/markup-rules/${editing}`, {
        min_price_usd: parseFloat(form.min_price_usd),
        max_price_usd: parseFloat(form.max_price_usd),
        markup_type: form.markup_type,
        markup_value: parseFloat(form.markup_value),
        max_price_ml: form.max_price_ml ? parseFloat(form.max_price_ml) : null,
        apply_now: applyNow,
        pilot_pct: pilotPct,
      });
      const pilot = (res as unknown as { pilot?: { id: number; pilot_pct: number } }).pilot;
      if (pilot) setNotice(`Piloto iniciado: el recargo nuevo aplica al ${pilot.pilot_pct}% de los productos del rango. En 72 horas vas a recibir la evaluación y acá aparecen los botones Extender / Revertir.`);
      else if (markupChanged && applyNow) setNotice('Regla aplicada a todos los productos del rango. El regulador de precios los reprecia en su próxima corrida (hasta 30 minutos).');
    } else {
      await api.post('/mercadolibre/markup-rules', {
        seller_id: sellerId,
        min_price_usd: parseFloat(form.min_price_usd),
        max_price_usd: parseFloat(form.max_price_usd),
        markup_type: form.markup_type,
        markup_value: parseFloat(form.markup_value),
        max_price_ml: form.max_price_ml ? parseFloat(form.max_price_ml) : null,
      });
    }
    setSaving(false);
    setSimOpen(false);
    setEditing(null);
    setShowAdd(false);
    setForm({ min_price_usd: '', max_price_usd: '', markup_type: 'percentage', markup_value: '', max_price_ml: '' });
    load();
  };

  const decideRollout = async (id: number, action: 'extend' | 'revert') => {
    const msg = action === 'extend'
      ? 'Aplicar el recargo nuevo a TODOS los productos del rango?'
      : 'Descartar el piloto y volver al recargo vigente para todos?';
    if (!confirm(msg)) return;
    const res = await api.post(`/mercadolibre/markup-rules/rollouts/${id}/${action}`, {});
    if (!res.success) setNotice(`No se pudo: ${(res as { error?: string }).error || 'error'}`);
    load();
  };

  const handleEdit = (rule: Rule) => {
    setEditing(rule.id);
    setForm({
      min_price_usd: String(rule.min_price_usd),
      max_price_usd: String(rule.max_price_usd),
      markup_type: rule.markup_type,
      markup_value: String(rule.markup_value),
      max_price_ml: rule.max_price_ml ? String(rule.max_price_ml) : '',
    });
    setShowAdd(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminar esta regla?')) return;
    await api.delete(`/mercadolibre/markup-rules/${id}`);
    load();
  };

  // Detect gaps between rules
  const sortedRules = [...rules].sort((a, b) => a.min_price_usd - b.min_price_usd);
  const gaps: string[] = [];
  for (let i = 0; i < sortedRules.length - 1; i++) {
    const currentMax = sortedRules[i].max_price_usd;
    const nextMin = sortedRules[i + 1].min_price_usd;
    if (nextMin > currentMax + 0.01) {
      gaps.push(`$${currentMax.toFixed(2)} — $${nextMin.toFixed(2)}`);
    }
  }

  if (loading) return <div className="flex justify-center py-8 text-[var(--color-text-muted)]"><RefreshCw className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">Reglas de Markup</h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Define el margen de ganancia por rango de precio Amazon. El campo "Max ML" define el techo para ML Pricing Automation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => { setShowAdd(true); setEditing(null); setForm({ min_price_usd: '', max_price_usd: '', markup_type: 'percentage', markup_value: '', max_price_ml: '' }); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90">
            <Plus size={14} /> Agregar Regla
          </button>
        </div>
      </div>

      {/* ML Pricing Automation Panel */}
      {autoStatus && (
        <div className={`p-4 border rounded-lg ${autoStatus.enabled ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20' : 'border-[var(--color-border)] bg-[var(--color-surface-hover)]'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text)]">ML Pricing Automation</h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                {autoStatus.enabled
                  ? `Activa: ${autoStatus.stats.active} site items con automation${autoStatus.stats.error > 0 ? `, ${autoStatus.stats.error} con error` : ''}`
                  : `Disponible para ${autoStatus.total_eligible} items elegibles. ML ajusta precios automaticamente para ganar competencia.`
                }
              </p>
            </div>
            <button onClick={handleToggleAutomation} disabled={autoLoading}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                autoStatus.enabled
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-green-600 text-white hover:bg-green-700'
              } disabled:opacity-50`}>
              {autoLoading ? 'Procesando...' : autoStatus.enabled ? 'Desactivar' : 'Activar'}
            </button>
          </div>
        </div>
      )}

      {/* Detail: Items en Automation + Elegibles */}
      {autoStatus && autoStatus.enabled && <AutomationItemsPanel sellerId={sellerId} onRefresh={load} />}

      {/* Gaps warning */}
      {gaps.length > 0 && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-600 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-yellow-800 dark:text-yellow-300">
            <AlertTriangle size={16} /> Gaps detectados en rangos de precio:
          </div>
          <div className="mt-1 text-xs text-yellow-700 dark:text-yellow-400">
            {gaps.map((g, i) => <span key={i} className="block">Rango sin cobertura: {g}</span>)}
          </div>
        </div>
      )}

      {/* Add/Edit form */}
      {showAdd && (
        <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface-hover)] space-y-3">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">{editing ? 'Editar Regla' : 'Nueva Regla'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Precio Min (USD)</label>
              <input type="number" step="0.01" value={form.min_price_usd} onChange={e => setForm(f => ({ ...f, min_price_usd: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Precio Max (USD)</label>
              <input type="number" step="0.01" value={form.max_price_usd} onChange={e => setForm(f => ({ ...f, max_price_usd: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm" placeholder="49.99" />
            </div>
            <div>
              <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Tipo</label>
              <select value={form.markup_type} onChange={e => setForm(f => ({ ...f, markup_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm">
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed">Fijo ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Markup</label>
              <input type="number" step="0.01" value={form.markup_value} onChange={e => setForm(f => ({ ...f, markup_value: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                placeholder={form.markup_type === 'percentage' ? '15' : '10.00'} />
            </div>
            <div>
              <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Max ML (USD)</label>
              <input type="number" step="0.01" value={form.max_price_ml} onChange={e => setForm(f => ({ ...f, max_price_ml: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                placeholder="Techo automation" title="Precio maximo de net_proceeds para ML Pricing Automation" />
            </div>
          </div>
          {/* Preview */}
          {form.min_price_usd && form.markup_value && (
            <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
              <span>
                Ejemplo: Amazon ${form.min_price_usd} → ML ${
                  form.markup_type === 'percentage'
                    ? (parseFloat(form.min_price_usd) * (1 + parseFloat(form.markup_value) / 100)).toFixed(2)
                    : (parseFloat(form.min_price_usd) + parseFloat(form.markup_value)).toFixed(2)
                }
              </span>
              <RulePreviewBadge sellerId={sellerId} min={parseFloat(form.min_price_usd)} max={parseFloat(form.max_price_usd || '999999')} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowAdd(false); setEditing(null); }}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)]">Cancelar</button>
            <button onClick={openSimulation} disabled={saving || !form.min_price_usd || !form.max_price_usd || !form.markup_value}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50">
              {saving ? 'Guardando...' : 'Ver efecto y guardar'}
            </button>
          </div>
        </div>
      )}

      {simOpen && (
        <MarkupSimulationModal
          sim={sim}
          loading={simLoading}
          markupChanged={!editing || markupChanged}
          saving={saving}
          pilotPct={pilotPct}
          onPilotPct={setPilotPct}
          onPilot={() => handleSave(false)}
          onApplyAll={() => handleSave(true)}
          onCancel={() => setSimOpen(false)}
        />
      )}

      {notice && (
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 text-sm text-blue-900 dark:text-blue-200 flex justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs underline">cerrar</button>
        </div>
      )}

      {/* Pilotos en curso / recientes (2026-09-18) */}
      {rollouts.filter((r) => r.status === 'pilot' || (r.evaluated_at && Date.now() - new Date(r.evaluated_at).getTime() < 7 * 86400e3)).length > 0 && (
        <div className="border border-[var(--color-border)] rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">Pilotos de recargo</h3>
          {rollouts.filter((r) => r.status === 'pilot' || (r.evaluated_at && Date.now() - new Date(r.evaluated_at).getTime() < 7 * 86400e3)).map((r) => (
            <div key={r.id} className="text-xs text-[var(--color-text-secondary)] border-t border-[var(--color-border)] pt-2 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[var(--color-text)]">${Number(r.range_min_usd ?? 0).toFixed(2)} — ${Number(r.range_max_usd ?? 0).toFixed(2)}</span>
                <span>{fmtMarkup(r.old_markup_type, r.old_markup_value)} → <b>{fmtMarkup(r.new_markup_type, r.new_markup_value)}</b></span>
                <span>· {r.pilot_pct}% de los productos</span>
                <span>· inició {fmtDate(r.started_at)}</span>
                <span className={`px-2 py-0.5 rounded font-medium ${r.status === 'pilot' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : r.status === 'extended' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                  {r.status === 'pilot' ? (r.evaluated_at ? 'evaluado, esperando decisión' : `en prueba, se evalúa ${fmtDate(r.evaluate_at)}`) : r.status === 'extended' ? 'extendido a todos' : 'revertido'}
                </span>
              </div>
              {r.pilot_metrics && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>Piloto ({r.pilot_metrics.pilot.asins} productos): ventas {r.pilot_metrics.pilot.units_before} → {r.pilot_metrics.pilot.units_after} ({fmtRatio(r.pilot_metrics.pilot.units_ratio)}), visitas {r.pilot_metrics.pilot.visits_before} → {r.pilot_metrics.pilot.visits_after} ({fmtRatio(r.pilot_metrics.pilot.visits_ratio)})</div>
                  <div>Resto ({r.pilot_metrics.control.asins} productos): ventas {r.pilot_metrics.control.units_before} → {r.pilot_metrics.control.units_after} ({fmtRatio(r.pilot_metrics.control.units_ratio)}), visitas {r.pilot_metrics.control.visits_before} → {r.pilot_metrics.control.visits_after} ({fmtRatio(r.pilot_metrics.control.visits_ratio)})</div>
                  {r.recommendation && <div className="md:col-span-2">Recomendación: <b className="uppercase">{r.recommendation}</b></div>}
                </div>
              )}
              {r.status === 'pilot' && (
                <div className="flex gap-2 pt-1">
                  <button onClick={() => decideRollout(r.id, 'extend')} className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700">Extender a todos</button>
                  <button onClick={() => decideRollout(r.id, 'revert')} className="px-3 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)]">Revertir</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rules table */}
      {rules.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <TrendingUp size={40} className="mx-auto mb-3 opacity-20" />
          <p>Sin reglas de markup configuradas</p>
        </div>
      ) : (
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium">Rango Precio (USD)</th>
                <th className="text-center px-4 py-2 text-[var(--color-text-muted)] font-medium">Tipo</th>
                <th className="text-center px-4 py-2 text-[var(--color-text-muted)] font-medium">Markup</th>
                <th className="text-center px-4 py-2 text-[var(--color-text-muted)] font-medium">Max ML</th>
                <th className="text-center px-4 py-2 text-[var(--color-text-muted)] font-medium">Ejemplo</th>
                <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedRules.map((rule) => {
                const examplePrice = (rule.min_price_usd + rule.max_price_usd) / 2;
                const mlPrice = rule.markup_type === 'percentage'
                  ? examplePrice * (1 + rule.markup_value / 100)
                  : examplePrice + rule.markup_value;

                return (
                  <tr key={rule.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]">
                    <td className="px-4 py-3 font-mono text-[var(--color-text)]">
                      ${Number(rule.min_price_usd).toFixed(2)} — ${Number(rule.max_price_usd).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rule.markup_type === 'percentage'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {rule.markup_type === 'percentage' ? '%' : '$'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-[var(--color-text)]">
                      {rule.markup_type === 'percentage' ? `${rule.markup_value}%` : `$${Number(rule.markup_value).toFixed(2)}`}
                      {rule.pilot_pct && rule.pilot_markup_value != null && (
                        <div className="text-xs font-normal text-yellow-700 dark:text-yellow-300" title="Piloto en curso: este valor aplica solo a una parte de los productos">
                          piloto {rule.pilot_pct}%: {fmtMarkup(String(rule.pilot_markup_type), rule.pilot_markup_value)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-[var(--color-text-muted)]">
                      {rule.max_price_ml ? `$${Number(rule.max_price_ml).toFixed(2)}` : <span className="opacity-40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-[var(--color-text-muted)]">
                      ${examplePrice.toFixed(2)} → ${mlPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleEdit(rule)} className="text-xs text-[var(--color-primary)] hover:underline mr-3">Editar</button>
                      <button onClick={() => handleDelete(rule.id)} className="text-xs text-red-400 hover:underline">Eliminar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
